import type { RootState } from '@reducers/index';
import { updateFilter, updateFilterLabel } from '@reducers/search';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import {
  AdTypes,
  CategoryID,
  CompositeFilter,
  ListFilter,
} from '@sbt-web/network/types';
import { Button } from '@sbt-web/ui';
import { wholeNumberDigit } from '@sbt-web/utils';
import { Category, isLeafCategory } from '@sbt-web/category-utils';
import CN from 'classnames';
import React from 'react';
import { batch, connect } from 'react-redux';
import { Filter } from './Filter';
import { FiltersStore } from '@tools/search/getFiltersStore';
import RadiusSelector from '../Elements/Filters/RadiusSelector';
import { GeoZoneSelector } from '../Elements/Filters/ZoneSelector';
import FiltersSkeletons from './FiltersSkeletons';

import classes from './FiltersContainer.module.scss';

interface OwnProps {
  onApply?: React.EventHandler<React.MouseEvent>;
}

interface StateProps {
  category: Category;
  adType: AdTypes;
  itemsTotal: number;
  activeFilters: { [key: string]: string };
  filtersStore: FiltersStore | null;
  filtersStoreLoading: boolean;
}

interface DispatchProps {
  updateFilterAndLabel: (
    key: string,
    value?: string,
    label?: string,
    uri?: string
  ) => void;
}

type Props = OwnProps & StateProps & DispatchProps;

const categoriesWithRadiusSearch: CategoryID[] = [
  CategoryID.Immobili,
  CategoryID.Appartamenti,
  CategoryID.CamerePostiLetto,
  CategoryID.Ville,
  CategoryID.TerreniRustici,
  CategoryID.GarageBox,
  CategoryID.LoftMansarde,
  CategoryID.CaseVacanza,
  CategoryID.UfficiLocali,
];
const isCategoryWithRadiusSearch = (category: Category): boolean =>
  categoriesWithRadiusSearch.includes(category.id);

export const FiltersContainer: React.FunctionComponent<Props> = (props) => {
  const { category, adType, itemsTotal, onApply, activeFilters } = props;
  const { isMobile } = useViewport();
  const { optimizely } = React.useContext(OptimizelySubitoContext);

  const trackSearchConversion = () => {
    optimizely?.onReady().then(() => {
      optimizely?.track('subito_filters_apply');
    });
  };

  const { filtersStore: currentConfig, filtersStoreLoading: configLoading } =
    props;

  const onFilterSelected =
    (filter: CompositeFilter) =>
    (key: string, value?: string, label?: string) => {
      // Clear dependent filters when a filter with dependencies is selected
      const dependentListFilters =
        currentConfig?.filters.filter(
          (f): f is ListFilter =>
            f.type === 'list' && (f.dependencies?.includes(filter.uri) ?? false)
        ) ?? [];

      for (const depFilter of dependentListFilters) {
        props.updateFilterAndLabel(
          depFilter.queryString,
          undefined,
          undefined,
          filter.uri
        );
      }

      props.updateFilterAndLabel(key, value, label, filter.uri);
      trackSearchConversion();
    };

  return (
    <div
      id="filters-container"
      className={CN(classes['container'], {
        [classes['in-dialog']]: isMobile,
      })}
    >
      <div className={classes.filters}>
        {!isMobile && (
          <h5 className={CN('headline-5', classes['filter-title'])}>
            Filtri ricerca
          </h5>
        )}
        {configLoading ? (
          <FiltersSkeletons size={isMobile ? 'large' : 'small'} />
        ) : (
          <>
            {!isMobile && isCategoryWithRadiusSearch(category) && (
              <RadiusSelector trackSearchConversion={trackSearchConversion} />
            )}
            {isLeafCategory(category) && category.zoneSupport && (
              <GeoZoneSelector trackSearchConversion={trackSearchConversion} />
            )}
            {currentConfig?.filters.map((filter) => (
              <Filter
                // category id and adType should be part of the key;
                // their value could have different associated valuelist.
                // so we need to trigger the re-render
                key={`${filter.uri}_${category.id}_${adType}`}
                filter={filter}
                category={category}
                onFilterSelected={onFilterSelected(filter)}
                activeFilters={activeFilters}
                trackSearchConversion={trackSearchConversion}
                filterStore={currentConfig}
              />
            ))}
            {isMobile && <div className={classes['btn-clearfix']} />}
          </>
        )}
      </div>
      {isMobile && (
        <div className={classes['btn-container']}>
          <Button onClick={onApply} classes={[classes['view-results']]}>
            <span className="button-text">
              Vedi {wholeNumberDigit.format(itemsTotal)} annunci
              {itemsTotal === 1 && 'o'}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default connect<StateProps, DispatchProps, unknown, RootState>(
  (state) => ({
    category: state.search.category,
    adType: state.search.adType,
    activeFilters: state.search.filters,
    itemsTotal: state.items.total,
    filtersStore: FiltersStore.fromSerialized(state.filtersConfig.filtersStore),
    filtersStoreLoading: state.filtersConfig.loading,
  }),
  (dispatch) => ({
    updateFilterAndLabel: (
      key: string,
      value: string = '',
      label: string = '',
      uri: string = ''
    ) => {
      batch(() => {
        dispatch(updateFilter({ key, value, uri }));
        dispatch(updateFilterLabel({ key, value: label }));
      });
    },
  })
)(FiltersContainer);
